import type { Language, QuestionCoaching } from './questionCoaching.types';

/** Generated once per app session — ensures pairs rotate every new session. */
const SESSION_SEED = Math.floor(Date.now() / 1000);

const STOPWORDS: Record<Language, Set<string>> = {
  en: new Set([
    'the', 'a', 'an', 'of', 'to', 'in', 'on', 'for', 'and', 'or', 'with',
    'is', 'are', 'was', 'were', 'be', 'been', 'being', 'you', 'your', 'must',
    'should', 'can', 'may', 'do', 'does', 'did', 'at', 'by', 'from', 'this',
    'that', 'these', 'those', 'it', 'as', 'if', 'then', 'when', 'while'
  ]),
  nl: new Set([
    'de', 'het', 'een', 'van', 'naar', 'in', 'op', 'voor', 'en', 'of', 'met',
    'is', 'zijn', 'was', 'waren', 'je', 'jij', 'uw', 'moet', 'mag', 'kan',
    'bij', 'door', 'uit', 'dit', 'dat', 'deze', 'die', 'als', 'dan', 'wanneer'
  ]),
  ar: new Set([
    'ال', 'في', 'على', 'من', 'إلى', 'عن', 'و', 'أو', 'هو', 'هي', 'هذا',
    'هذه', 'ذلك', 'تلك', 'يجب', 'يمكن', 'قد', 'مع', 'لك', 'ل', 'ب', 'عن'
  ])
};

const RULE_KEYWORDS: Record<Language, string[]> = {
  en: ['must', 'should', 'always', 'never', 'only', 'stop', 'yield', 'right of way', 'proceed'],
  nl: ['moet', 'mag', 'altijd', 'nooit', 'alleen', 'stop', 'voorrang', 'doorgaan'],
  ar: ['يجب', 'لا', 'فقط', 'توقف', 'أولوية', 'تابع']
};

const tokenize = (text: string): string[] => {
  return (text || '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, '')
    .split(/\s+/)
    .filter(Boolean);
};

const _extractKeyword = (text: string, language: Language, maxWords: number = 2): string => {
  const tokens = tokenize(text).filter(token => !STOPWORDS[language].has(token));
  const picked = tokens.slice(0, maxWords);
  if (picked.length === 0) {
    return language === 'nl' ? 'de regel' : language === 'ar' ? 'القاعدة' : 'the rule';
  }
  return picked.join(' ');
};

const limitWords = (text: string, maxWords: number): string => {
  const words = (text || '').split(/\s+/).filter(Boolean);
  if (words.length <= maxWords) return text;
  let shortened = words.slice(0, maxWords).join(' ');
  shortened = shortened.replace(/\s+(and|or|the|a|an|if|unless|that|which|when|where|but|so|to|for|of|in|on|at|by|with|from|into|upon|than|via)$/i, '');
  if (!shortened.match(/[.!?]$/)) {
    shortened += '.';
  }
  return shortened;
};

/** User card + matching friendly coach response (Round 1 reflective pairs). */
export type ReflectivePair = { prompt: string; coach: string };

/** Pool of 25 reflective Round 1 pairs: user prompt + matching coach reply. Short and friendly. */
const getReflectiveRound1PairsPool = (language: Language, category?: 'priority' | 'speed' | 'signs' | 'roundabout' | 'parking' | 'generic'): ReflectivePair[] => {
  // Category-specific prompts for better context awareness
  const getPriorityPrompts = (lang: Language): ReflectivePair[] => {
    if (lang === 'nl') {
      return [
        { prompt: 'Ik vergat de voorrang', coach: 'Let op wie voorrang heeft. Hier is de regel.' },
        { prompt: 'Ik zag de voorrang niet', coach: 'Voorrang is cruciaal. Hier is de regel.' },
        { prompt: 'Ik dacht dat ik voorrang had', coach: 'Voorrangsregels zijn belangrijk. Hier is de juiste.' }
      ];
    }
    if (lang === 'ar') {
      return [
        { prompt: 'نسيت الأولوية', coach: 'انتبه لمن له الأولوية. إليك القاعدة.' },
        { prompt: 'لم أر الأولوية', coach: 'الأولوية حاسمة. إليك القاعدة.' },
        { prompt: 'ظننت أن لي الأولوية', coach: 'قواعد الأولوية مهمة. إليك القاعدة الصحيحة.' }
      ];
    }
    return [
      { prompt: 'Forgot the priority', coach: 'Focus on who has priority. Here is the rule.' },
      { prompt: "Didn't see the priority", coach: 'Priority is crucial. Here is the rule.' },
      { prompt: 'Thought I had priority', coach: 'Priority rules are important. Here is the correct one.' }
    ];
  };

  const getSpeedPrompts = (lang: Language): ReflectivePair[] => {
    if (lang === 'nl') {
      return [
        { prompt: 'Ik reed te snel', coach: 'Snelheidslimieten zijn belangrijk. Hier is de regel.' },
        { prompt: 'Ik zag de snelheid niet', coach: 'Let op de snelheidslimiet. Hier is de regel.' },
        { prompt: 'Ik dacht dat het sneller mocht', coach: 'Snelheidsregels zijn duidelijk. Hier is de juiste.' }
      ];
    }
    if (lang === 'ar') {
      return [
        { prompt: 'كنت أسرع من اللازم', coach: 'حدود السرعة مهمة. إليك القاعدة.' },
        { prompt: 'لم أر السرعة', coach: 'انتبه لحد السرعة. إليك القاعدة.' },
        { prompt: 'ظننت أن السرعة مسموحة', coach: 'قواعد السرعة واضحة. إليك القاعدة الصحيحة.' }
      ];
    }
    return [
      { prompt: 'Was driving too fast', coach: 'Speed limits are important. Here is the rule.' },
      { prompt: "Didn't see the speed", coach: 'Watch the speed limit. Here is the rule.' },
      { prompt: 'Thought it was faster', coach: 'Speed rules are clear. Here is the correct one.' }
    ];
  };

  const getSignsPrompts = (lang: Language): ReflectivePair[] => {
    if (lang === 'nl') {
      return [
        { prompt: 'Ik begreep het bord niet', coach: 'Verkeersborden zijn belangrijk. Hier is de betekenis.' },
        { prompt: 'Ik zag het bord niet', coach: 'Let op verkeersborden. Hier is de regel.' },
        { prompt: 'Ik vergat het bord', coach: 'Borden zijn cruciaal. Hier is de betekenis.' }
      ];
    }
    if (lang === 'ar') {
      return [
        { prompt: 'لم أفهم اللافتة', coach: 'اللافتات مهمة. إليك المعنى.' },
        { prompt: 'لم أر اللافتة', coach: 'انتبه لللافتات. إليك القاعدة.' },
        { prompt: 'نسيت اللافتة', coach: 'اللافتات حاسمة. إليك المعنى.' }
      ];
    }
    return [
      { prompt: "Didn't understand the sign", coach: 'Traffic signs are important. Here is the meaning.' },
      { prompt: "Didn't see the sign", coach: 'Watch for traffic signs. Here is the rule.' },
      { prompt: 'Forgot the sign', coach: 'Signs are crucial. Here is the meaning.' }
    ];
  };

  const getRoundaboutPrompts = (lang: Language): ReflectivePair[] => {
    if (lang === 'nl') {
      return [
        { prompt: 'Ik vergat de rotonde regel', coach: 'Rotonderegels zijn specifiek. Hier is de regel.' },
        { prompt: 'Ik had geen voorrang op rotonde', coach: 'Voorrang op rotonde is belangrijk. Hier is de regel.' },
        { prompt: 'Ik reed de rotonde verkeerd', coach: 'Rotonde-rijden is een vaardigheid. Hier is de regel.' }
      ];
    }
    if (lang === 'ar') {
      return [
        { prompt: 'نسيت قاعدة الدوار', coach: 'قواعد الدوار محددة. إليك القاعدة.' },
        { prompt: 'لم يكن لي أولوية في الدوار', coach: 'الأولوية في الدوار مهمة. إليك القاعدة.' },
        { prompt: 'سقت الدوار بشكل خاطئ', coach: 'القيادة في الدوار مهارة. إليك القاعدة.' }
      ];
    }
    return [
      { prompt: 'Forgot roundabout rule', coach: 'Roundabout rules are specific. Here is the rule.' },
      { prompt: 'No priority on roundabout', coach: 'Priority on roundabout is important. Here is the rule.' },
      { prompt: 'Drove roundabout wrong', coach: 'Roundabout driving is a skill. Here is the rule.' }
    ];
  };

  const getParkingPrompts = (lang: Language): ReflectivePair[] => {
    if (lang === 'nl') {
      return [
        { prompt: 'Ik parkeerde verkeerd', coach: 'Parkeerregels zijn belangrijk. Hier is de regel.' },
        { prompt: 'Ik zag de parkeerregel niet', coach: 'Let op parkeerborden. Hier is de regel.' },
        { prompt: 'Ik vergat de parkeerplaats', coach: 'Parkeerregels zijn duidelijk. Hier is de juiste.' }
      ];
    }
    if (lang === 'ar') {
      return [
        { prompt: 'ركنت بشكل خاطئ', coach: 'قواعد الركن مهمة. إليك القاعدة.' },
        { prompt: 'لم أر قاعدة الركن', coach: 'انتبه للافتات الركن. إليك القاعدة.' },
        { prompt: 'نسيت مكان الركن', coach: 'قواعد الركن واضحة. إليك القاعدة الصحيحة.' }
      ];
    }
    return [
      { prompt: 'Parked incorrectly', coach: 'Parking rules are important. Here is the rule.' },
      { prompt: "Didn't see parking rule", coach: 'Watch for parking signs. Here is the rule.' },
      { prompt: 'Forgot parking spot', coach: 'Parking rules are clear. Here is the correct one.' }
    ];
  };

  // Start with category-specific prompts if available
  const categoryPrompts = category ? {
    priority: getPriorityPrompts(language),
    speed: getSpeedPrompts(language),
    signs: getSignsPrompts(language),
    roundabout: getRoundaboutPrompts(language),
    parking: getParkingPrompts(language),
    generic: []
  }[category] : [];

  // Generic prompts — expanded pool for maximum variety across sessions
  const genericPrompts = language === 'nl' ? [
    { prompt: 'Ik gokte', coach: 'Geen probleem—hier is de regel voor deze.' },
    { prompt: 'Ik had geen idee', coach: 'Kan gebeuren. Dit is wat hier telt.' },
    { prompt: 'Ik las het verkeerd', coach: 'Makkelijk te doen. Hier is de regel.' },
    { prompt: 'Ik was te snel', coach: 'Neem volgende keer de tijd. Hier is de regel.' },
    { prompt: 'Ik vergiste me in de regel', coach: 'Komt voor. Hier is de juiste regel.' },
    { prompt: 'Ik was onzeker', coach: 'Geen probleem. Hier is de regel om te onthouden.' },
    { prompt: 'Ik vergat de regel', coach: 'Gebeurt. Hier is de regel nog eens.' },
    { prompt: 'Ik was in de war', coach: 'Geen zorgen. Hier is de regel helder.' },
    { prompt: 'Ik was gewoon moe', coach: 'Rust helpt. Voor nu: hier is de regel.' },
    { prompt: 'Ik was er zeker van', coach: 'Zeker zijn is goed; hier is de regel voor volgende keer.' },
    { prompt: 'Het kon me niet schelen', coach: 'Deze telt voor het examen. Hier is de regel.' },
    { prompt: 'Ik was afgeleid', coach: 'Gebeurt. Hier is wat je hier moet onthouden.' },
    { prompt: 'Ik verveelde me', coach: 'Laten we deze pakken—hier is de regel.' },
    { prompt: 'De tijd was op', coach: 'Begrepen. Hier is de regel voor de volgende keer.' },
    { prompt: 'Ik dacht te diep na', coach: 'Soms is de regel simpel. Hier is hij.' },
    { prompt: 'Ik lette niet op', coach: 'Geen probleem. Hier is wat hier telt.' },
    { prompt: 'Ik twijfelde aan mezelf', coach: 'Vertrouw op je voorbereiding. Hier is de regel.' },
    { prompt: 'Ik sloeg het lezen over', coach: 'Makkelijk te doen. Hier is de regel voor deze.' },
    { prompt: 'Ik was niet geconcentreerd', coach: 'Geen probleem. Hier is de regel om te focussen.' },
    { prompt: 'Ik dacht dat het anders was', coach: 'Komt voor. Hier is de juiste interpretatie.' },
    { prompt: 'Ik was te zelfverzekerd', coach: 'Goed vertrouwen, maar hier is de regel.' },
    { prompt: 'Ik zag het detail niet', coach: 'Makkelijk te missen. Hier is het belangrijkste detail.' },
    { prompt: 'Ik was aan het dagdromen', coach: 'Gebeurt. Hier is de regel om wakker te blijven.' },
    { prompt: 'Ik wist het niet meer', coach: 'Geen zorgen. Hier is de opfrissing.' },
    { prompt: 'Ik klikte verkeerd', coach: 'Makkelijk gedaan. Hier is de regel.' },
    { prompt: 'Ik raakte in paniek', coach: 'Adem even. Dit is wat telt.' },
    { prompt: 'Ik dacht dat ik het wist', coach: 'Bijna! Hier is de precieze regel.' },
    { prompt: 'De formulering verwaarde me', coach: 'Goed gezien. Hier is de duidelijkste versie.' },
    { prompt: 'Ik nam een aanname', coach: 'Aannames pakken je. Hier is de regel.' },
    { prompt: 'Ik veranderde van gedachten', coach: 'Eerste instinct is vaak goed. Hier is de regel.' },
    { prompt: 'Ik begreep de vraag niet', coach: 'Lees volgende keer goed. Hier is het kernpunt.' },
    { prompt: 'Ik dacht aan een andere regel', coach: 'Makkelijk te verwarren. Hier is deze.' },
    { prompt: 'Vergeten van de vorige keer', coach: 'Herhaling helpt. Hier is de regel.' },
    { prompt: 'Ik was gestrest', coach: 'Adem—het wordt makkelijker. Hier is de regel.' },
    { prompt: 'Ik paste de regel verkeerd toe', coach: 'Bijna! Zo pas je het toe.' },
    { prompt: 'Ik ging op gevoel', coach: 'Regels winnen hier van gevoel. Hier is de juiste.' },
    { prompt: 'Ik las niet alle opties', coach: 'Lees alle keuzes. Hier is de regel.' },
    { prompt: 'Breinmist', coach: 'Gebeurt. Hier is de regel nog eens.' },
    { prompt: 'Ik dacht dat het duidelijk was', coach: 'Lastige vragen verstoppen zich. Hier is de regel.' },
    { prompt: 'De afbeelding verwaarde me', coach: 'Hier is de bijbehorende regel.' },
    { prompt: 'Ik had een hint nodig', coach: 'Hier is je hint—dit is de regel.' },
    { prompt: 'Ik koos te snel', coach: 'Neem even de tijd. Hier is de regel.' },
    { prompt: 'Ik verwisselde opties', coach: 'Makkelijke vergissing. Hier is de juiste.' },
    { prompt: 'Ik miste de context', coach: 'Goed gemerkt. Hier is de volledige regel.' },
    { prompt: 'Ik redeneerde verkeerd', coach: 'Bijna goed. Hier is de correcte redenering.' },
    { prompt: 'Ik was er bijna', coach: 'Bijna! Hier is het verschil.' },
    { prompt: 'Automatisch geantwoord', coach: 'Neem de tijd. Hier is de regel.' },
    { prompt: 'Ik combineerde regels', coach: 'Begrijpelijk. Hier is de juiste.' },
    { prompt: 'Ik was verbaasd', coach: 'Deze verrast velen. Hier is de regel.' }
  ] : language === 'ar' ? [
    { prompt: 'خمّنت', coach: 'لا بأس—إليك القاعدة لهذه.' },
    { prompt: 'لم أكن أعرف', coach: 'يحدث. هذا ما يهم هنا.' },
    { prompt: 'أخطأت في القراءة', coach: 'سهل الحدوث. إليك القاعدة.' },
    { prompt: 'استعجلت', coach: 'خذ وقتك المرة القادمة. إليك القاعدة.' },
    { prompt: 'خلطت القاعدة', coach: 'شائع. إليك القاعدة الصحيحة.' },
    { prompt: 'لم أكن متأكداً', coach: 'لا مشكلة. إليك القاعدة للتذكر.' },
    { prompt: 'كنت مرتبكاً', coach: 'لا قلق. إليك القاعدة بوضوح.' },
    { prompt: 'كنت متعباً فقط', coach: 'الراحة تساعد. للآن: إليك القاعدة.' },
    { prompt: 'كنت متأكداً', coach: 'التأكد جيد؛ إليك القاعدة للمرة القادمة.' },
    { prompt: 'لم أهتم', coach: 'هذه مهمة في الامتحان. إليك القاعدة.' },
    { prompt: 'انشغلت', coach: 'يحدث. إليك ما يجب تذكره هنا.' },
    { prompt: 'كنت أتسلى فقط', coach: 'لنضبط هذه—إليك القاعدة.' },
    { prompt: 'انتهى الوقت', coach: 'مفهوم. إليك القاعدة لتكون جاهزاً.' },
    { prompt: 'فكّرت أكثر من اللازم', coach: 'أحياناً القاعدة بسيطة. ها هي.' },
    { prompt: 'لم أنتبه', coach: 'لا مشكلة. إليك ما يهم هنا.' },
    { prompt: 'شككت في نفسي', coach: 'ثق بدراستك. إليك القاعدة.' },
    { prompt: 'تخطيت القراءة', coach: 'سهل الحدوث. إليك القاعدة لهذه.' },
    { prompt: 'لم أكن مركزاً', coach: 'لا مشكلة. إليك القاعدة للتركيز.' },
    { prompt: 'ظننت أنها مختلفة', coach: 'يحدث. إليك التفسير الصحيح.' },
    { prompt: 'كنت واثقاً جداً', coach: 'الثقة جيدة، لكن إليك القاعدة.' },
    { prompt: 'لم أر التفصيل', coach: 'سهل تفويته. إليك التفصيل الرئيسي.' },
    { prompt: 'كنت أحلم', coach: 'يحدث. إليك القاعدة لتبقى مستيقظاً.' },
    { prompt: 'نسيتها', coach: 'لا مشكلة. إليك التذكير.' },
    { prompt: 'ضغطت غلط', coach: 'سهل الحدوث. إليك القاعدة.' },
    { prompt: 'أصابني ذعر', coach: 'خذ نفساً. هذا ما يهم.' },
    { prompt: 'ظننت أنني أعرف', coach: 'تقريباً! إليك القاعدة الدقيقة.' },
    { prompt: 'الصياغة أربكتني', coach: 'ملاحظة جيدة. إليك الصيغة الأوضح.' },
    { prompt: 'افترضت شيئاً', coach: 'الافتراضات تخدعنا. إليك القاعدة.' },
    { prompt: 'غيّرت رأيي', coach: 'الحدس الأول أصح. إليك القاعدة.' },
    { prompt: 'لم أفهم السؤال', coach: 'اقرأ بعناية. إليك النقطة الرئيسية.' },
    { prompt: 'فكّرت في قاعدة أخرى', coach: 'سهل الخلط. إليك هذه القاعدة.' },
    { prompt: 'نسيت من المرة الماضية', coach: 'التكرار يثبّتها. إليك القاعدة.' },
    { prompt: 'كنت متوتراً', coach: 'تنفّس—يصبح أسهل. إليك القاعدة.' },
    { prompt: 'طبّقت القاعدة خطأ', coach: 'قريب! هكذا تطبّقها.' },
    { prompt: 'سرت على الإحساس', coach: 'القواعد تتفوق هنا. إليك الصحيحة.' },
    { prompt: 'لم أقرأ كل الخيارات', coach: 'اقرأ الكل. إليك القاعدة.' },
    { prompt: 'ضباب في الذهن', coach: 'يحدث. إليك القاعدة مجدداً.' },
    { prompt: 'ظننت أنها واضحة', coach: 'الصعبة تختبئ. إليك القاعدة.' },
    { prompt: 'الصورة أربكتني', coach: 'إليك القاعدة المتعلقة بها.' },
    { prompt: 'احتجت لتلميح', coach: 'إليك تلميحك—هذه هي القاعدة.' },
    { prompt: 'اخترت بسرعة', coach: 'تمهّل قليلاً. إليك القاعدة.' },
    { prompt: 'خلطت الخيارات', coach: 'زلة بسيطة. إليك الصحيحة.' },
    { prompt: 'فاتني السياق', coach: 'ملاحظة جيدة. إليك القاعدة الكاملة.' },
    { prompt: 'استنتجت بشكل خاطئ', coach: 'قريب. إليك الاستنتاج الصحيح.' },
    { prompt: 'كنت على وشك الإجابة', coach: 'تقريباً! إليك الفرق.' },
    { prompt: 'أجبت تلقائياً', coach: 'خذ وقتك. إليك القاعدة.' },
    { prompt: 'جمعت قواعد', coach: 'مفهوم. إليك القاعدة الصحيحة.' },
    { prompt: 'فاجأتني', coach: 'تفاجئ الكثيرين. إليك القاعدة.' }
  ] : [
    { prompt: 'I guessed', coach: "No worries—let's get the rule for this one." },
    { prompt: 'I had no idea', coach: "That's okay. Here's what matters here." },
    { prompt: 'I misread it', coach: "Easy to do. Here's the rule for this one." },
    { prompt: 'I rushed', coach: "Take your time next time. Here's the rule." },
    { prompt: 'I mixed up the rule', coach: "Common. Let's lock in the right rule." },
    { prompt: 'I was unsure', coach: "No problem. Here's the rule to remember." },
    { prompt: 'I forgot the rule', coach: "Happens. Here's the rule again." },
    { prompt: 'I was confused', coach: "No worries. Here's the rule clearly." },
    { prompt: 'Just tired', coach: "Rest helps. For now, here's the rule." },
    { prompt: 'I was sure', coach: "Being sure is good; here's the rule for next time." },
    { prompt: "Didn't care", coach: "This one matters for the exam. Here's the rule." },
    { prompt: 'Got distracted', coach: "Happens. Here's what to remember here." },
    { prompt: 'Just bored', coach: "Let's nail this one—here's the rule." },
    { prompt: 'Ran out of time', coach: "Understood. Here's the rule so you're set." },
    { prompt: 'Overthought it', coach: "Sometimes the rule is simple. Here it is." },
    { prompt: "Wasn't paying attention", coach: "No problem. Here's what counts here." },
    { prompt: 'Second-guessed myself', coach: "Trust your study. Here's the rule." },
    { prompt: 'Skipped reading', coach: "Easy to do. Here's the rule for this one." },
    { prompt: 'Was not focused', coach: "No problem. Here's the rule to focus on." },
    { prompt: 'Thought it was different', coach: "Happens. Here's the right interpretation." },
    { prompt: 'Was too confident', coach: "Good confidence, but here's the rule." },
    { prompt: 'Missed the detail', coach: "Easy to miss. Here's the key detail." },
    { prompt: 'Was daydreaming', coach: "Happens. Here's the rule to stay alert." },
    { prompt: 'Forgot it', coach: "No worries. Here's the reminder." },
    { prompt: 'Clicked the wrong one', coach: "Easy slip. Here's the rule." },
    { prompt: 'I panicked', coach: "Take a breath. Here's what matters." },
    { prompt: 'Thought I knew it', coach: "Almost! Here's the precise rule." },
    { prompt: 'The wording tripped me', coach: "Good catch. Here's the clearest version." },
    { prompt: 'Made an assumption', coach: "Assumptions catch us out. Here's the rule." },
    { prompt: 'I changed my mind', coach: "First instinct is often right. Here's the rule." },
    { prompt: 'Misunderstood the question', coach: "Read carefully next time. Here's the key point." },
    { prompt: 'Was thinking of another rule', coach: "Easy to mix up. Here's this one." },
    { prompt: 'Forgot from last time', coach: "Repetition fixes it. Here's the rule." },
    { prompt: 'I was stressed', coach: "Breathe—it gets easier. Here's the rule." },
    { prompt: "Didn't apply the rule right", coach: "Close! Here's how to apply it." },
    { prompt: 'Was going by feel', coach: "Rules beat instinct here. Here's the right one." },
    { prompt: "Didn't read all options", coach: "Read all choices next time. Here's the rule." },
    { prompt: 'Brain fog', coach: "Happens. Here's the rule once more." },
    { prompt: 'Thought it was obvious', coach: "Tricky ones hide in plain sight. Here's the rule." },
    { prompt: 'The image confused me', coach: "Here's the rule that goes with it." },
    { prompt: 'Needed a hint', coach: "Here's your hint—the rule is this." },
    { prompt: 'Chose too quickly', coach: "Take a moment. Here's the rule." },
    { prompt: 'Mixed up the options', coach: "Easy mistake. Here's the correct one." },
    { prompt: 'Missed the context', coach: "Good notice. Here's the full rule." },
    { prompt: 'Reasoned it wrong', coach: "Nearly there. Here's the right reasoning." },
    { prompt: 'Was almost right', coach: "Almost! Here's the difference." },
    { prompt: 'Answered on autopilot', coach: "Slow down a touch. Here's the rule." },
    { prompt: 'Blended two rules', coach: "Understandable. Here's the right one." },
    { prompt: 'This surprised me', coach: "Surprises many. Here's the rule." }
  ];

  // Combine category-specific and generic prompts
  return [...categoryPrompts, ...genericPrompts];
};

/** Simple seeded shuffle (Fisher–Yates) for deterministic variety per question. */
function shuffleWithSeed<T>(array: T[], seed: number): T[] {
  const out = array.slice();
  for (let i = out.length - 1; i > 0; i--) {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    const j = seed % (i + 1);
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/** djb2-style hash so similar ids (e.g. q-priority-1 vs q-priority-2) get very different seeds. */
function hashString(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h * 33) ^ s.charCodeAt(i)) >>> 0;
  }
  return h;
}

/** Pool of motivational move-on messages (shuffled per question). */
const getMoveOnPool = (language: Language): string[] => {
  if (language === 'nl') {
    return [
      'Je doet het geweldig! Tik op Doorgaan als je klaar bent.',
      'Ga zo door! Tik op Doorgaan als je klaar bent.',
      'Je kunt het! Tik op Doorgaan als je klaar bent.',
      'Mooie voortgang! Tik op Doorgaan als je klaar bent.',
      'Nog een stap verder! Tik op Doorgaan als je klaar bent.',
      'Top! Tik op Doorgaan als je klaar bent.',
      'Blijf zo doorgaan—je leert! Tik op Doorgaan als je klaar bent.',
      'Lekker bezig! Tik op Doorgaan als je klaar bent.',
      'Goed gedaan! Tik op Doorgaan als je klaar bent.',
      'Je bent op de goede weg! Tik op Doorgaan als je klaar bent.'
    ];
  }
  if (language === 'ar') {
    return [
      'أنت ممتاز! اضغط على متابعة عندما تكون جاهزاً.',
      'استمر! اضغط على متابعة عندما تكون جاهزاً.',
      'أنت قادر! اضغط على متابعة عندما تكون جاهزاً.',
      'تقدم رائع! اضغط على متابعة عندما تكون جاهزاً.',
      'خطوة أقرب! اضغط على متابعة عندما تكون جاهزاً.',
      'رائع! اضغط على متابعة عندما تكون جاهزاً.',
      'واصل—أنت تتعلم! اضغط على متابعة عندما تكون جاهزاً.',
      'ممتاز! اضغط على متابعة عندما تكون جاهزاً.',
      'أحسنت! اضغط على متابعة عندما تكون جاهزاً.',
      'أنت على الطريق الصحيح! اضغط على متابعة عندما تكون جاهزاً.'
    ];
  }
  return [
    "You're doing great! Tap Continue when you're ready.",
    "Keep it up! Tap Continue when you're ready.",
    "You've got this! Tap Continue when you're ready.",
    "Nice progress! Tap Continue when you're ready.",
    "One step closer! Tap Continue when you're ready.",
    "Brilliant! Tap Continue when you're ready.",
    "Keep going—you're learning! Tap Continue when you're ready.",
    "On fire! Tap Continue when you're ready.",
    "Well done! Tap Continue when you're ready.",
    "You're on track! Tap Continue when you're ready."
  ];
};

/** Pick one motivational move-on message (shuffle pool by questionId + questionIndex so each question gets a different message). */
export function getMoveOnMessage(language: Language, questionId?: string, questionIndex?: number): string {
  const pool = getMoveOnPool(language);
  if (pool.length === 0) return language === 'nl' ? 'Tik op Doorgaan als je klaar bent.' : language === 'ar' ? 'اضغط على متابعة عندما تكون جاهزاً.' : "Tap Continue when you're ready.";
  const seedInput = (questionId ?? '') + '_move_' + String(questionIndex ?? 0);
  const seed = seedInput ? hashString(seedInput) : Math.floor(Date.now() / 10000);
  const shuffled = shuffleWithSeed([...pool], seed);
  return shuffled[0];
}

/** Return 2 reflective prompt–coach pairs for Round 1 (shuffled from pool; order of cards also randomized). */
function getReflectiveRound1Pairs(language: Language, questionId?: string, questionIndex?: number, questionText?: string, subject?: string): [ReflectivePair, ReflectivePair] {
  // Detect category from question text and subject for context awareness
  const useCategoryAwarePrompts = true;
  const category = useCategoryAwarePrompts && (questionText || subject)
    ? detectCategory((questionText || '') + ' ' + (subject || ''), language)
    : 'generic';
  const pool = getReflectiveRound1PairsPool(language, category);
  if (pool.length < 2) {
    const fallback = pool[0] ?? { prompt: '', coach: '' };
    return [fallback, fallback];
  }
  // Seed from questionId + SESSION_SEED so same question gets different pairs every new session
  const seedInput = (questionId ?? '') + String(SESSION_SEED);
  const seed = hashString(seedInput);
  const shuffled = shuffleWithSeed(pool, seed);
  const pair1 = shuffled[0];
  const pair2 = shuffled[1];
  // Randomize which card is first so order varies per question
  const swap = (seed >>> 16) % 2 === 0;
  return swap ? [pair1, pair2] : [pair2, pair1];
}



const detectCategory = (text: string, language: Language): 'priority' | 'speed' | 'signs' | 'roundabout' | 'parking' | 'generic' => {
  const lower = (text || '').toLowerCase();
  if (language === 'nl') {
    if (/rotonde/.test(lower)) return 'roundabout';
    if (/snelheid|km\/?u|limiet/.test(lower)) return 'speed';
    if (/bord|borden|verkeersbord/.test(lower)) return 'signs';
    if (/parkeren|parkeer/.test(lower)) return 'parking';
    if (/voorrang|haaientand|stopbord|rechts/.test(lower)) return 'priority';
    return 'generic';
  }
  if (language === 'ar') {
    if (/دوار|ميدان/.test(lower)) return 'roundabout';
    if (/سرعة|كم\/?س|حد/.test(lower)) return 'speed';
    if (/لافتة|إشارة|علامة/.test(lower)) return 'signs';
    if (/وقوف|ركن/.test(lower)) return 'parking';
    if (/أولوية|أعطِ|اعط|توقف|يمين/.test(lower)) return 'priority';
    return 'generic';
  }
  if (/roundabout/.test(lower)) return 'roundabout';
  if (/speed|km\/?h|limit/.test(lower)) return 'speed';
  if (/sign|traffic sign/.test(lower)) return 'signs';
  if (/parking|park/.test(lower)) return 'parking';
  if (/priority|right of way|give way|yield|stop sign/.test(lower)) return 'priority';
  return 'generic';
};

const splitSentences = (text: string): string[] => {
  const clean = (text || '').replace(/\s+/g, ' ').trim();
  if (!clean) return [];
  return clean.split(/[.!?؟]/).map(s => s.trim()).filter(Boolean);
};

const pickRuleSentence = (text: string): string => {
  const sentences = splitSentences(text);
  return sentences[0] || (text || '').trim();
};

const _pickSecondSentence = (text: string): string => {
  const sentences = splitSentences(text);
  return sentences[1] || sentences[0] || (text || '').trim();
};

const _normalizeText = (text: string): string => {
  return (text || '').toLowerCase().replace(/\s+/g, ' ').trim();
};

const TRAILING_TRIM = /\s+(and|or|the|a|an|if|unless|that|which|when|where|but|so|to|for|of|in|on|at|by|with|from|into|upon|than|via)$/i;

const limitWordsForTakeHome = (text: string, maxWords: number): string => {
  const words = (text || '').split(/\s+/).filter(Boolean);
  if (words.length <= maxWords) {
    const t = (text || '').trim();
    return /[.!?]$/.test(t) ? t : t + '.';
  }
  let shortened = words.slice(0, maxWords).join(' ');
  while (TRAILING_TRIM.test(shortened)) {
    shortened = shortened.replace(TRAILING_TRIM, '');
  }
  if (!/[.!?]$/.test(shortened)) shortened += '.';
  return shortened;
};

const buildStrictTakeHome = (explanation: string, language: Language): string => {
  const sentences = splitSentences(explanation);
  if (sentences.length === 0) return '';
  const ruleKeywords = RULE_KEYWORDS[language];
  const ruleSentence = sentences.find(sentence =>
    ruleKeywords.some(keyword => sentence.toLowerCase().includes(keyword))
  );
  if (!ruleSentence) return '';
  return limitWordsForTakeHome(ruleSentence, 18);
};

const buildPriorityRuleHint = (explanation: string, language: Language): string => {
  const lower = (explanation || '').toLowerCase();
  if (language === 'nl') {
    if (lower.includes('tram')) return 'Trams hebben voorrang bij kruisingen.';
    if (lower.includes('rechts')) return 'Geef voorrang aan verkeer van rechts.';
    if (lower.includes('rotonde')) return 'Verkeer op de rotonde heeft voorrang.';
    if (lower.includes('stop')) return 'Stop volledig en geef voorrang.';
    if (lower.includes('voorrang')) return 'Let op de voorrangsregel.';
    return 'Let op wie voorrang heeft.';
  }
  if (language === 'ar') {
    if (lower.includes('ترام')) return 'للمترو أولوية عند التقاطعات.';
    if (lower.includes('يمين')) return 'أعطِ أولوية لليمين.';
    if (lower.includes('دوار')) return 'الأولوية لمن في الدوار.';
    if (lower.includes('توقف')) return 'توقف تماماً ثم أعطِ الأولوية.';
    if (lower.includes('أولوية')) return 'انتبه لقاعدة الأولوية.';
    return 'انتبه لمن له الأولوية.';
  }
  if (lower.includes('tram')) return 'Trams have priority at junctions.';
  if (lower.includes('right')) return 'Give way to traffic from the right.';
  if (lower.includes('roundabout')) return 'Traffic on the roundabout has priority.';
  if (lower.includes('stop')) return 'Stop fully and give way.';
  if (lower.includes('priority')) return 'Focus on who has priority.';
  return 'Focus on who has priority.';
};

const _buildRound1Response = (
  language: Language,
  category: 'priority' | 'speed' | 'signs' | 'roundabout' | 'parking' | 'generic',
  rule: string,
  explanation: string
): string => {
  if (category === 'priority') {
    return limitWords(buildPriorityRuleHint(explanation, language), 12);
  }
  const baseRule = limitWords(rule || pickRuleSentence(explanation), 12);
  if (language === 'nl') {
    return limitWords(`Belangrijk: ${baseRule}`, 12);
  }
  if (language === 'ar') {
    return limitWords(`مهم: ${baseRule}`, 12);
  }
  return limitWords(`Key point: ${baseRule}`, 12);
};

/** Second reflective hint for Round 1 card 2. Never factual; always reflective/supportive. */
const _buildRound1Hint2 = (
  language: Language,
  category: 'priority' | 'speed' | 'signs' | 'roundabout' | 'parking' | 'generic',
  keyword: string
): string => {
  if (language === 'nl') {
    if (category === 'priority') return 'Denk aan wie voorrang heeft.';
    return limitWords(`Houd ${keyword || 'de regel'} in gedachten.`, 8);
  }
  if (language === 'ar') {
    if (category === 'priority') return 'فكّر بمن له الأولوية.';
    return limitWords(`تذكّر: ${keyword || 'القاعدة'}`, 8);
  }
  if (category === 'priority') return 'Consider who has priority.';
  return limitWords(`Keep ${keyword || 'the rule'} in mind.`, 8);
};

const pickSentenceByKeyword = (
  explanation: string,
  keywordSource: string,
  language: Language
): string => {
  const sentences = splitSentences(explanation);
  if (sentences.length === 0) return '';
  const keywordTokens = tokenize(keywordSource).filter(token => !STOPWORDS[language].has(token));
  if (keywordTokens.length === 0) {
    return '';
  }
  const match = sentences.find(sentence =>
    keywordTokens.some(token => sentence.toLowerCase().includes(token))
  );
  return match || '';
};

const _buildStrictFactualResponse = (
  explanation: string,
  keywordSource: string,
  maxWords: number,
  language: Language
): string => {
  const sentence = pickSentenceByKeyword(explanation, keywordSource, language);
  if (!sentence) return '';
  const wordCount = sentence.split(/\s+/).filter(Boolean).length;
  if (wordCount > maxWords) {
    return '';
  }
  return sentence.trim();
};

export const generatePerQuestionCoaching = ({
  questionId,
  questionText,
  subject,
  correctAnswer: _correctAnswer,
  explanation,
  takeHomeRule: _takeHomeRule,
  language,
  questionIndex
}: {
  questionId: string;
  questionText: string;
  subject: string;
  correctAnswer: string;
  explanation: string;
  takeHomeRule: string;
  language: Language;
  questionIndex?: number;
}): QuestionCoaching => {
  const explanationSource = (explanation || '').trim() || (questionText || '').trim();
  const strictTakeHome = buildStrictTakeHome(explanationSource, language);
  const [pair1, pair2] = getReflectiveRound1Pairs(language, questionId, questionIndex, questionText, subject);
  const moveOn = getMoveOnMessage(language, questionId, questionIndex);

  return {
    questionId,
    questionText,
    subject,
    round1: {
      card1: { prompt: pair1.prompt, coach: pair1.coach },
      card2: { prompt: pair2.prompt, coach: pair2.coach }
    },
    takeHome: strictTakeHome,
    moveOn
  };
};
